import { HttpClient } from '@angular/common/http';

import {
    addCouponCode,
    addGiftCard,
    addGiftWrappingOptions,
    addToBasket,
    loadBasket,
    removeCouponCode,
    removeFromBasket,
    removeGiftCard,
    removeGiftWrappingOptions,
    setDeliveryMethod,
    setUsePoints,
    setZipCode,
    updateGiftWrappingOptions
} from '../action-handlers';
import { BasketActionResult, CancellableRequest, CxsBasket, CxsBasketLineType, errorMessages } from '../model';
import { getBasket as apiGetBasket, putBasket as apiPutBasket } from '../services';
import {
    asyncActionWithRetry,
    isBrowser,
    isOlderBasket,
    isSynchronized,
    mapBasketToUpdate,
    toCancellableRequest
} from '../utils';

type BasketState = { current: CxsBasket | undefined; synchronized: Promise<CxsBasket> };

type Basket = {
    loadComplete: Promise<CxsBasket>;
    hasRequestInProgress: () => boolean;
    getBasket: () => BasketState;
    updateMembershipEmail: (email: string) => void;
    updateBasket: (customerEmail: string) => BasketActionResult;
    addToBasket: (
        productId: string,
        quantityToAdd: number,
        giftWrappingId?: string,
        giftCardId?: string,
        giftCardText?: string
    ) => BasketActionResult;
    removeFromBasket: (productId: string) => BasketActionResult;
    setZipCode: (zipCode: string) => BasketActionResult;
    setDeliveryMethod: (deliveryMethod: string) => BasketActionResult;
    addGiftWrappingOptions(
        productId: string,
        giftWrappingId?: string,
        giftCardId?: string,
        giftCardText?: string
    ): BasketActionResult;
    removeGiftWrappingOptions(productId: string, type: CxsBasketLineType): BasketActionResult;
    updateGiftWrappingOptions(
        productId: string,
        newGiftWrappingId?: string,
        newGiftCardId?: string,
        newGiftCardText?: string
    ): BasketActionResult;
    addCouponCode(code: string): BasketActionResult;
    removeCouponCode(code: string): BasketActionResult;
    addGiftCard(code: string): BasketActionResult;
    removeGiftCard(code: string): BasketActionResult;
    setUsePoints(usePoints: boolean): BasketActionResult;
    isSynchronized: () => boolean;
    reSynchronize: (basket?: CxsBasket, shouldPush?: boolean, customerEmail?: string) => BasketActionResult;
    destroy: () => void;
};

type BasketConfig = {
    window: Window | undefined;
    existingBasket?: CxsBasket;
    httpClient: HttpClient;
    getLatestModifiedDate: () => string;
    customerEmail?: string;
};

export const initializeBasket = (config: BasketConfig): Basket => {
    if (!isBrowser(config.window) || !config.window) {
        const emptyPromise = new Promise<any>(() => {});
        const emptyBasketState = { current: undefined, synchronized: emptyPromise };
        const emptyAction = { localUpdate: undefined, synchronized: emptyPromise };
        return {
            loadComplete: emptyPromise,
            hasRequestInProgress: () => false,
            getBasket: () => emptyBasketState,
            updateMembershipEmail: () => emptyAction,
            updateBasket: () => emptyAction,
            addToBasket: () => emptyAction,
            removeFromBasket: () => emptyAction,
            setZipCode: () => emptyAction,
            addGiftWrappingOptions: () => emptyAction,
            removeGiftWrappingOptions: () => emptyAction,
            updateGiftWrappingOptions: () => emptyAction,

            destroy: () => undefined,
            isSynchronized: () => true,
            reSynchronize: () => emptyAction,
            addCouponCode: () => emptyAction,
            removeCouponCode: () => emptyAction,
            addGiftCard: () => emptyAction,
            removeGiftCard: () => emptyAction,
            setDeliveryMethod: () => emptyAction,
            setUsePoints: () => emptyAction
        };
    }

    const { existingBasket, httpClient, getLatestModifiedDate } = config;

    let basketInstance: CxsBasket | undefined;
    let activePutRequest: CancellableRequest<CxsBasket> | undefined;
    let activeGetRequest: CancellableRequest<CxsBasket> | undefined;
    let isDestroyed = false;

    const getCurrentBasket = () => basketInstance;
    const getMembershipEmail = () => config.customerEmail;

    const destroy = () => {
        if (activeGetRequest) {
            activeGetRequest.cancel();
            activeGetRequest = undefined;
        }
        if (activePutRequest) {
            activePutRequest.cancel();
            activePutRequest = undefined;
        }
        basketInstance = undefined;
        isDestroyed = true;
    };

    const updateMembershipEmail = (email: string) => {
        config.customerEmail = email;
    };

    const pushBasket = async (basket: CxsBasket) => {
        if (activePutRequest) {
            activePutRequest.cancel();
        }
        activePutRequest = toCancellableRequest(
            apiPutBasket(httpClient, mapBasketToUpdate(basket), getMembershipEmail())
        );
        const synchedBasket = await activePutRequest.request;
        activePutRequest = undefined;
        if (!isOlderBasket(getCurrentBasket(), synchedBasket)) {
            basketInstance = synchedBasket;
        }
    };

    const pullBasket = async (): Promise<CxsBasket> => {
        if (activeGetRequest) {
            const activeRequestRef = activeGetRequest;
            activeGetRequest = undefined;
            activeRequestRef.cancel();
        }
        activeGetRequest = toCancellableRequest(
            apiGetBasket(httpClient, getCurrentBasket()?.basketId, getMembershipEmail())
        );
        const result = await activeGetRequest.request;
        activeGetRequest = undefined;
        if (!result) {
            throw Error(errorMessages.failedToLoadBasket);
        }
        return result;
    };

    const setBasket = async (basket: CxsBasket, sync: boolean): Promise<CxsBasket> => {
        if (isOlderBasket(getCurrentBasket(), basket)) {
            throw Error(errorMessages.cannotSetOlderBasket);
        }

        basketInstance = basket;
        if (sync) {
            try {
                await pushBasket(basket);
            } catch (err) {
                if (err !== errorMessages.asyncOperationCancelled) {
                    throw err;
                }
            }
        }
        return getCurrentBasket() || basket;
    };

    const loadComplete = asyncActionWithRetry(
        () => loadBasket(existingBasket, httpClient, getMembershipEmail()),
        3,
        () => isDestroyed
    );
    loadComplete
        .then(basket => {
            if (isDestroyed) {
                throw Error(errorMessages.basketDestroyed);
            }
            setBasket(basket, false);
        })
        .catch(() => {});

    const whenReady = (): Promise<CxsBasket> => {
        let resolve: (value: CxsBasket | PromiseLike<CxsBasket>) => void;
        const promise = new Promise<CxsBasket>(res => (resolve = res));
        loadComplete.then(() => {
            const basket = getCurrentBasket();
            if (!basket) {
                return;
            }
            if (!isSynchronized(getLatestModifiedDate(), basket)) {
                pullBasket()
                    .then(resolve)
                    .catch(_ => {});
            } else {
                resolve(basket);
            }
        });
        return promise;
    };

    const whenSynchronized = (actionHandler: (basket: CxsBasket) => CxsBasket, push = true) => {
        const rollbackBasket = basketInstance;

        if (!basketInstance || !isSynchronized(getLatestModifiedDate(), basketInstance)) {
            let resolve: (value: CxsBasket | PromiseLike<CxsBasket>) => void;
            const nextSync = new Promise<CxsBasket>(r => (resolve = r));
            whenReady().then(basket => {
                setBasket(actionHandler(basket), push)
                    .then(resolve)
                    .catch(() => {
                        if (rollbackBasket) {
                            setBasket(rollbackBasket, false);
                        }
                    });
            });
            return { localUpdate: undefined, synchronized: nextSync };
        } else {
            const newBasket = actionHandler(basketInstance);
            const sync = setBasket(newBasket, push);
            sync.catch(() => {
                if (rollbackBasket) {
                    setBasket(rollbackBasket, false);
                }
            });
            return { localUpdate: newBasket, synchronized: sync };
        }
    };

    const reSynchronize = (basket?: CxsBasket, shouldPush = false) => {
        if (activeGetRequest) {
            activeGetRequest.cancel();
            activeGetRequest = undefined;
        }
        if (activePutRequest) {
            activePutRequest.cancel();
            activePutRequest = undefined;
        }

        if (basket) {
            return { localUpdate: undefined, synchronized: setBasket(basket, shouldPush) };
        } else {
            return whenSynchronized(syncBasket => syncBasket, shouldPush);
        }
    };

    return {
        loadComplete,
        destroy,
        reSynchronize,
        getBasket: () => ({ current: getCurrentBasket(), synchronized: whenReady() }),
        updateMembershipEmail,
        updateBasket: () => whenSynchronized(basket => basket, true),
        isSynchronized: () => isSynchronized(getLatestModifiedDate(), getCurrentBasket()),
        hasRequestInProgress: () => !!activePutRequest || !!activeGetRequest,
        addToBasket: (productId: string, quantityToAdd: number, giftWrappingId, giftCardId, giftCardText) =>
            whenSynchronized(basket =>
                addToBasket(basket, productId, quantityToAdd, false, giftWrappingId, giftCardId, giftCardText)
            ),
        addGiftWrappingOptions: (productId: string, giftWrappingId: string, giftCardId: string, giftMessage: string) =>
            whenSynchronized(basket =>
                addGiftWrappingOptions(basket, productId, giftWrappingId, giftCardId, giftMessage)
            ),
        removeGiftWrappingOptions: (productId: string, type: CxsBasketLineType) =>
            whenSynchronized(basket => removeGiftWrappingOptions(basket, productId, type)),
        updateGiftWrappingOptions: (
            productId: string,
            newGiftWrappingId: string,
            newGiftCardId: string,
            newGiftCardText: string
        ) =>
            whenSynchronized(basket =>
                updateGiftWrappingOptions(basket, productId, newGiftWrappingId, newGiftCardId, newGiftCardText)
            ),
        removeFromBasket: (productId: string) => whenSynchronized(basket => removeFromBasket(basket, productId)),
        addCouponCode: (code: string) => whenSynchronized(basket => addCouponCode(basket, code)),
        removeCouponCode: (code: string) => whenSynchronized(basket => removeCouponCode(basket, code)),
        addGiftCard: (code: string) => whenSynchronized(basket => addGiftCard(basket, code)),
        removeGiftCard: (code: string) => whenSynchronized(basket => removeGiftCard(basket, code)),
        setZipCode: (zipCode: string) => whenSynchronized(basket => setZipCode(basket, zipCode)),
        setDeliveryMethod: (deliveryMethod: string) =>
            whenSynchronized(basket => setDeliveryMethod(basket, deliveryMethod)),
        setUsePoints: (usePoints: boolean) => whenSynchronized(basket => setUsePoints(basket, usePoints))
    };
};
